import { Currencies, CurrenciesProvider, CurrenciesProviderHelpers } from '@/entities/curency'
import { formatDate } from '@/shared/lib/date'
import { RootState } from '@/store/store'
import { createSelector } from '@reduxjs/toolkit'
import { ExchangeRatesModel } from '@/entities/exchangeRates'

const selectRates = (state: RootState) => state.rates.rates

export const selectRatesInfo = createSelector([selectRates], (rates) => {
  const value = rates ?? []

  const payIn = value.find(
    (r) => r.currencyFrom === Currencies.RUB && r.currencyTo === Currencies.USDT
  )
  const payOut = value.find(
    (r) => r.currencyFrom === Currencies.USDT && r.currencyTo === Currencies.RUB
  )

  const lastUpdatedRaw = payIn?.lastUpdatedAt || payOut?.lastUpdatedAt || ''

  const formattedDate = lastUpdatedRaw
    ? formatDate(lastUpdatedRaw, 'withoutSeconds', {}, true)
    : '—'

  const formattedTime = lastUpdatedRaw ? formatDate(lastUpdatedRaw, 'timeOnly') : '—'

  return {
    payInRate: payIn ? `${payIn.rate} RUB` : '—',
    payOutRate: payOut ? `${payOut.rate} RUB` : '—',
    lastUpdated: formattedDate,
    lastUpdatedShort: formattedTime,
  }
})

interface RatesSelectorProps {
  currencyRateAddPercent: number | null
  currencyRateProvides: CurrenciesProvider
}

export const selectRatesInfoByProvider = ({
  currencyRateAddPercent,
  currencyRateProvides,
}: RatesSelectorProps) =>
  createSelector([selectRates], (rates) => {
    const value = rates ?? []

    const providerMeta = CurrenciesProviderHelpers.getById(currencyRateProvides)

    // фильтруем только по выбранному провайдеру
    const providerRates = value.filter((r) => r.name === providerMeta.name)

    const payIn = providerRates.find(
      (r) => r.currencyFrom === Currencies.RUB && r.currencyTo === Currencies.USDT
    )
    const payOut = providerRates.find(
      (r) => r.currencyFrom === Currencies.USDT && r.currencyTo === Currencies.RUB
    )

    const lastUpdatedRaw = payIn?.lastUpdatedAt || payOut?.lastUpdatedAt || ''

    const formattedDate = lastUpdatedRaw
      ? formatDate(lastUpdatedRaw, 'withoutSeconds', {}, true)
      : '—'

    const formattedTime = lastUpdatedRaw ? formatDate(lastUpdatedRaw, 'timeOnly') : '—'

    // функция применения коэффициента
    const applyPercent = (rate?: number) => {
      if (rate == null) return undefined
      if (!currencyRateAddPercent) return rate

      return +(rate * (1 + currencyRateAddPercent / 100)).toFixed(4)
    }

    const payInRate = payIn ? `${applyPercent(payIn.rate)} RUB` : '—'
    const payOutRate = payOut ? `${applyPercent(payOut.rate)} RUB` : '—'

    return {
      provider: providerMeta.displayName,
      payInRate,
      payOutRate,
      lastUpdated: formattedDate,
      lastUpdatedShort: formattedTime,
    }
  })
export const selectProviderRatesInfo = createSelector(
  [(state: RootState) => state.rates.rates],
  (rates) => {
    const value = rates ?? []

    // Фильтруем только нужные провайдеры
    const allowedProviders = ['Rapira']
    const filteredRates = value.filter((rate) => allowedProviders.includes(rate.name))

    // Группируем по провайдерам
    const ratesByProvider = filteredRates.reduce(
      (acc, rate) => {
        if (!acc[rate.name]) {
          acc[rate.name] = {}
        }

        if (rate.currencyFrom === Currencies.RUB && rate.currencyTo === Currencies.USDT) {
          acc[rate.name].payIn = rate
        } else if (rate.currencyFrom === Currencies.USDT && rate.currencyTo === Currencies.RUB) {
          acc[rate.name].payOut = rate
        }

        return acc
      },
      {} as Record<string, { payIn?: ExchangeRatesModel; payOut?: ExchangeRatesModel }>
    )

    // Форматируем результат для каждого провайдера
    return Object.entries(ratesByProvider).map(([name, rates]) => {
      const lastUpdatedRaw = rates.payIn?.lastUpdatedAt || rates.payOut?.lastUpdatedAt || ''

      const formattedDate = lastUpdatedRaw
        ? formatDate(lastUpdatedRaw, 'withoutSeconds', {}, true)
        : '—'
      const formattedTime = lastUpdatedRaw ? formatDate(lastUpdatedRaw, 'timeOnly') : '—'

      return {
        name,
        payInRate: rates.payIn ? `${rates.payIn.rate} RUB` : '—',
        payOutRate: rates.payOut ? `${rates.payOut.rate} RUB` : '—',
        lastUpdated: formattedDate,
        lastUpdatedShort: formattedTime,
        lastUpdatedRaw,
      }
    })
  }
)
